import { useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { Card, Text, Button, Flex, Select, TextInput } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { ArenaLockUnlockUploadModal } from "./components/ArenaLockUnlockConfiguration";
import { ENVIRONMENT_OPTIONS, ArenaLockAction } from "./constants";
import * as styles from "./styles_vanilla.css";
import type { Arena } from "./types";

export const ArenaLock = () => {
  const history = useHistory();
  const { url } = useRouteMatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedArena, setSelectedArena] = useState<Arena | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadModalAction, setUploadModalAction] = useState<ArenaLockAction>(
    ArenaLockAction.LOCK,
  );
  const [selectedEnvironment, setSelectedEnvironment] = useState("test");

  const handleContinue = () => {
    if (!searchTerm.trim()) return;

    const arena: Arena = {
      id: searchTerm.trim(),
      name: searchTerm.trim(),
      environment: selectedEnvironment,
    };

    setSelectedArena(arena);
    setUploadModalAction(ArenaLockAction.LOCK);
    setIsUploadModalOpen(true);
  };

  const handleUploadSuccess = (_jobId: string, _executionArn: string) => {
    setIsUploadModalOpen(false);
    setSelectedArena(null);

    const dashboardPath = url.replace("/arena-lock", "");
    history.push(dashboardPath);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  return (
    <div className={styles.container} data-testid="data-studio-arena-lock">
      <div className={styles.header}>
        <Card className="inset-lg">
          <Flex alignItems="center" gap={Sizes.MD} className={styles.headerContent}>
            <Icon type="lock" size="large" color={Colors.Primary600} />
            <Text variant="h2">Arena Lock Management</Text>
          </Flex>
          <Text variant="secondary-md" className={styles.headerDescription}>
            Search for arenas and manage lock operations
          </Text>
        </Card>
      </div>

      <div className={styles.searchSection}>
        <Card className="inset-md">
          <Flex alignItems="center" gap={Sizes.MD} className={styles.searchHeader}>
            <Icon type="lock" size="medium" color={Colors.Primary600} />
            <Text variant="h4">Arena Domain Input</Text>
          </Flex>

          <Flex gap={Sizes.SM} alignItems="center" className={styles.searchControls}>
            <div className={styles.searchInputWrapper}>
              <TextInput
                icon="edit"
                placeholder="Enter arena domain (e.g., 'test-arena')"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                isFullWidth
              />
            </div>
            <Select
              value={selectedEnvironment}
              onChange={(value) => setSelectedEnvironment(String(value))}
            >
              <Select.Section>
                {ENVIRONMENT_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
            <Button variant="primary" onClick={handleContinue} disabled={!searchTerm.trim()}>
              Continue
            </Button>
          </Flex>
        </Card>
      </div>

      {selectedArena && isUploadModalOpen && (
        <ArenaLockUnlockUploadModal
          isOpen={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          onSuccess={handleUploadSuccess}
          arenaDomain={selectedArena.id}
          environment={selectedEnvironment}
          action={uploadModalAction}
          arenaName={selectedArena.name}
          userEmail=""
        />
      )}
    </div>
  );
};

export default ArenaLock;
